import React, { useState, useEffect } from 'react'
import { Viewer, Worker } from '@react-pdf-viewer/core'
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout'
import FileViewer from 'react-file-viewer'
import mammoth from 'mammoth'
import { client } from '../Client'
import './DocViewerStyles.css'  // Import the CSS file

// Import required CSS
import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/default-layout/lib/styles/index.css'

function renderDocViewer(lection) {
  const [docContent, setDocContent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fileExtension = client.extractFileExt(lection.content.name)
  const fileUrl = lection.content.url
  const fileName = lection.content.name

  // Create plugin instance for PDF viewer optimized for mobile
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [], // No sidebar on mobile
    toolbarPlugin: {
      fullScreenPlugin: {
        enableShortcuts: false,
      },
      getFilePlugin: {
        fileNameGenerator: () => fileName,
      },
      printPlugin: {
        enableShortcuts: false,
      },
      propertiesPlugin: {
        enableShortcuts: false,
      },
      rotatePlugin: {
        enableShortcuts: false,
      },
      searchPlugin: {
        enableShortcuts: false,
      },
    },
  })

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true)
      setError(null)

      try {
        if (fileExtension === 'docx') {
          // Handle DOCX files with mammoth
          const response = await fetch(fileUrl)
          const arrayBuffer = await response.arrayBuffer()
          const result = await mammoth.convertToHtml({ arrayBuffer })
          setDocContent(result.value)
        }
        setLoading(false)
      } catch (err) {
        setError(err.message)
        setLoading(false)
      }
    }

    if (fileExtension === 'docx') {
      loadContent()
    } else {
      setLoading(false)
    }
  }, [fileUrl, fileExtension])

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only"></span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='text-center p-3'>
        <span className="text-danger">
          {window.t('document.error_loading_file')}: {error}
          <br />
          <a href="#" onClick={(e) => { client.downloadLectionFile(e, lection) }}>{fileName}</a>
        </span>
      </div>
    )
  }

  if (!client.checkReactViewerExt(fileName)) {
    return (
      <div className='text-center p-3'>
        <span className="text-danger">
          {window.t('document.file_format_not_supported')}
          <a href="#" onClick={(e) => { client.downloadLectionFile(e, lection) }}>{fileName}</a>
        </span>
      </div>
    )
  }

  // Handle different file types for mobile
  switch (fileExtension.toLowerCase()) {
    case 'pdf':
      return (
        <Worker workerUrl="/pdf.worker.min.js">
          <div style={{ minHeight: '100vh', border: '1px solid rgba(0, 0, 0, 0.3)', position: 'relative', zIndex: 1001 }}>
            <Viewer
              fileUrl={fileUrl}
              plugins={[defaultLayoutPluginInstance]}
            />
          </div>
        </Worker>
      )

    case 'docx':
      return (
        <div className="document-viewer-content" style={{ padding: '15px', minHeight: '100vh', fontSize: '14px' }}>
          <div dangerouslySetInnerHTML={{ __html: docContent }} />
        </div>
      )

    case 'doc':
      // For DOC files, show download link on mobile
      return (
        <div className='text-center p-4'>
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">
                <i className="fas fa-file-word fa-2x mb-2 text-primary"></i>
                <br />
                {fileName}
              </h6>
              <p className="card-text text-muted small">
                {window.t('document.file_format_not_supported')}
              </p>
              <a
                href="#"
                className="btn btn-primary btn-sm"
                style={{ position: 'relative', zIndex: 1000 }}
                onClick={(e) => { client.downloadLectionFile(e, lection) }}
              >
                <i className="fas fa-download me-1"></i>
                {window.t('document.download_file')}
              </a>
            </div>
          </div>
        </div>
      )

    case 'xlsx':
    case 'xls':
    case 'pptx':
    case 'ppt':
      // For Office files that can't be previewed directly on mobile
      return (
        <div className='text-center p-4'>
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">
                <i className="fas fa-file-alt fa-2x mb-2 text-primary"></i>
                <br />
                {fileName}
              </h6>
              <p className="card-text text-muted small">
                {window.t('document.file_format_not_supported')}
              </p>
              <a
                href="#"
                className="btn btn-primary btn-sm"
                style={{ position: 'relative', zIndex: 1000 }}
                onClick={(e) => { client.downloadLectionFile(e, lection) }}
              >
                <i className="fas fa-download me-1"></i>
                {window.t('document.download_file')}
              </a>
            </div>
          </div>
        </div>
      )

    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'tiff':
    case 'tif':
    case 'bmp':
    case 'webp':
      return (
        <div className="text-center" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
          <img
            src={fileUrl}
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
          <div style={{ display: 'none' }} className="text-danger p-3">
            {window.t('document.image_load_error')}
            <br />
            <a href="#" onClick={(e) => { client.downloadLectionFile(e, lection) }}>{fileName}</a>
          </div>
        </div>
      )

    case 'txt':
    case 'html':
    case 'htm':
      return (
        <iframe
          src={fileUrl}
          style={{
            width: '100%',
            minHeight: '100vh',
            border: 'none',
            backgroundColor: 'white'
          }}
          title={fileName}
          sandbox="allow-same-origin"
        />
      )

    default:
      // Mobile fallback - show download option
      return (
        <div className='text-center p-4'>
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">
                <i className="fas fa-file fa-2x mb-2 text-muted"></i>
                <br />
                {fileName}
              </h6>
              <p className="card-text text-muted small">
                {window.t('document.file_format_not_supported')}
              </p>
              <a
                href="#"
                className="btn btn-primary btn-sm"
                style={{ position: 'relative', zIndex: 1000 }}
                onClick={(e) => { client.downloadLectionFile(e, lection) }}
              >
                <i className="fas fa-download me-1"></i>
                {window.t('document.download_file')}
              </a>
            </div>
          </div>
        </div>
      )
  }
}

const ContentDocument = React.memo(props => {
  const lection = props.lection

  return (
    <div className="player lecture-content player-text">
      {renderDocViewer(lection)}
    </div>
  )
})

export default ContentDocument